import { createFileRoute, redirect } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Plus, Trash2, DollarSign, FileText, Clock } from 'lucide-react';

import { Page } from '@/components/layout/Page';
import { Section } from '@/components/layout/Section';
import { Grid } from '@/components/layout/Grid';
import { Stack } from '@/components/layout/Stack';
import { PageHeader } from '@/components/composed/PageHeader';
import {
  StatusBadge,
  type StatusTone,
} from '@/components/composed/StatusBadge';
import { MoneyCell } from '@/components/composed/MoneyCell';
import { DateCell } from '@/components/composed/DateCell';
import { IdCell } from '@/components/composed/IdCell';
import { MaskedCell } from '@/components/composed/MaskedCell';
import { EmptyState } from '@/components/composed/EmptyState';
import { Timeline } from '@/components/composed/Timeline';
import { Stepper } from '@/components/composed/Stepper';
import { Dropzone } from '@/components/composed/Dropzone';
import { ConfirmDialog } from '@/components/composed/ConfirmDialog';
import { FilterBar, FilterField } from '@/components/composed/FilterBar';
import { notify } from '@/components/composed/notify';
import { RailSubNav } from '@/components/composed/RailSubNav';
import { DownloadCell } from '@/components/composed/DownloadCell';
import { ImportReviewTable } from '@/components/composed/ImportReviewTable';
import {
  BankAccountsSection,
  type BankAccountRow,
} from '@/components/composed/BankAccountsSection';
import { NestedDetailTabs } from '@/components/composed/NestedDetailTabs';
import type { EntitySection } from '@/lib/entity-sections';
import type { ArtifactState } from '@/lib/artifact-state';
import type { ImportReviewRow } from '@/lib/import-review';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Switch } from '@/components/ui/switch';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet,
} from '@/components/ui/field';
import { Textarea } from '@/components/ui/textarea';
import { DatePicker } from '@/components/composed/DatePicker';
import {
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxContent,
  ComboboxInput,
  ComboboxItem,
  ComboboxValue,
} from '@/components/ui/combobox';
import { Combobox, MultiCombobox } from '@/components/composed/Combobox';
import { CardRadioGroup } from '@/components/composed/CardRadioGroup';
import {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from '@/components/ui/command';
import { Slider } from '@/components/ui/slider';
import { AddressFields } from '@/components/composed/AddressFields';
import { KPICard } from '@/components/composed/KPICard';
import { DisbursementModePicker } from '@/features/billing';
import { usePageMeta } from '@/app/shell/use-page-meta';

/*
 * Kitchen sink (dev-only). A living showcase of the composed layer + key
 * primitives, rendered with real design-system tokens. Purpose: visual QA and a
 * reference for what exists — especially Stepper/Dropzone, which have no feature
 * consumer yet. Not a product feature: it composes shared UI, so it lives as a
 * route (not under features/), and it is gated out of production builds.
 */
export const Route = createFileRoute('/_auth/kitchen-sink')({
  beforeLoad: () => {
    // Never ship the showcase to production.
    if (import.meta.env.PROD) {
      throw redirect({ to: '/' });
    }
  },
  component: KitchenSinkPage,
});

const TONES: StatusTone[] = [
  'neutral',
  'success',
  'warning',
  'info',
  'danger',
  'purple',
  'orange',
];

// --- Mock data for the entity-slice component demos (no network) ---

const DEMO_SECTIONS: EntitySection[] = [
  { id: 'profile', label: 'Profile' },
  { id: 'bank-accounts', label: 'Bank Accounts' },
  { id: 'members', label: 'Members' },
  { id: 'contracts', label: 'Contracts' },
];

const DOWNLOAD_STATES: Array<{ state: ArtifactState; note: string }> = [
  { state: 'completed', note: 'signed URL resolved on click' },
  { state: 'failed', note: 'surfaces the run error' },
  { state: 'pending', note: 'polling / busy spinner' },
];

const IMPORT_ROWS: ImportReviewRow[] = [
  { id: 'r1', label: 'ACME-HEALTH-2026-01.csv', status: 'ok' },
  {
    id: 'r2',
    label: 'Row 42 — missing routing number',
    status: 'warning',
    detail: 'Defaulted to primary account.',
  },
  {
    id: 'r3',
    label: 'Row 87 — unmatched member',
    status: 'error',
    detail: 'No member found for SSN ending 4321.',
  },
];

const BANK_ACCOUNTS: BankAccountRow[] = [
  {
    id: 'b1',
    bankName: 'First National',
    routingNumber: '021000021',
    accountNumber: '000123456789',
    category: 'Operating',
    accountType: 'Checking',
    isPrimary: true,
    status: 'active',
  },
  {
    id: 'b2',
    bankName: 'Second State',
    routingNumber: '011401533',
    accountNumber: '000987654321',
    category: 'Reserve',
    accountType: 'Savings',
    status: 'inactive',
  },
];

function ShowcaseCard({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

/*
 * In-page section header for the showcase. The page's single <h1> is the
 * PageHeader "Kitchen Sink" title; each showcase region gets an <h2> so the
 * heading order is h1 -> h2 (region) -> h3 (component-internal) with no skipped
 * level. Local to this dev-only page — PageHeader is reserved for the one page
 * title, never a section header.
 */
function SectionHeader({
  title,
  description,
}: {
  title: string;
  description?: string;
}) {
  return (
    <div className="flex flex-col gap-1">
      <h2 className="text-xl font-semibold tracking-tight">{title}</h2>
      {description ? (
        <p className="text-sm text-muted-foreground">{description}</p>
      ) : null}
    </div>
  );
}

function KitchenSinkPage() {
  usePageMeta({ title: 'Kitchen Sink' });

  const [step, setStep] = useState(1);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [files, setFiles] = useState<string[]>([]);
  const [plan, setPlan] = useState('monthly');
  const [autopay, setAutopay] = useState(true);
  const [scope, setScope] = useState('all');
  const [date, setDate] = useState<string>('2026-01-15');
  const [fruit, setFruit] = useState<string | null>(null);
  const [fruits, setFruits] = useState<string[]>(['Apple', 'Cherry']);
  const [notes, setNotes] = useState('');
  const [cardPlan, setCardPlan] = useState<string>('starter');
  const [mode, setMode] = useState<'NET' | 'GROSS' | 'DIRECT'>('NET');
  const [volume, setVolume] = useState<number[]>([40]);
  const [priceRange, setPriceRange] = useState<number[]>([20, 80]);
  const [lastCommand, setLastCommand] = useState<string | null>(null);
  const [commandOpen, setCommandOpen] = useState(false);
  // Demo-only: open the palette with ⌘K (mac) / Ctrl+K. A real app-global
  // shortcut would live in the app shell with the palette mounted once, not on
  // this showcase route.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setCommandOpen((open) => !open);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);
  const [address, setAddress] = useState({
    street: '',
    city: '',
    region: '',
    postalCode: '',
    country: '',
  });

  return (
    <Page bleed>
      <PageHeader
        title="Kitchen Sink"
        description="Dev-only showcase of composed components, primitives, and tokens. Not shipped to production."
      />

      <Grid cols={2} gap={6}>
        <ShowcaseCard title="Buttons">
          <div className="flex flex-wrap items-center gap-2">
            <Button>Default</Button>
            <Button variant="secondary">Secondary</Button>
            <Button variant="outline">Outline</Button>
            <Button variant="ghost">Ghost</Button>
            <Button variant="destructive">Destructive</Button>
            <Button size="sm">
              <Plus className="size-4" />
              Small
            </Button>
            <Button disabled>Disabled</Button>
          </div>
        </ShowcaseCard>

        <ShowcaseCard title="Status tones (7)">
          <div className="flex flex-wrap gap-2">
            {TONES.map((tone) => (
              <StatusBadge key={tone} tone={tone} label={tone} />
            ))}
          </div>
          <Separator className="my-4" />
          <div className="flex flex-wrap gap-2">
            <Badge>Badge</Badge>
            <Badge variant="secondary">Secondary</Badge>
            <Badge variant="outline">Outline</Badge>
            <Badge variant="destructive">Destructive</Badge>
          </div>
        </ShowcaseCard>

        <ShowcaseCard title="Cells (mono / money / masked / date)">
          <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
            <span className="text-muted-foreground">IdCell</span>
            <IdCell value="INV-1001" muted={false} />
            <span className="text-muted-foreground">MoneyCell</span>
            <MoneyCell amountMinor={12345} currency="USD" />
            <span className="text-muted-foreground">MaskedCell (TIN)</span>
            <MaskedCell value="12-3456789" />
            <span className="text-muted-foreground">DateCell</span>
            <DateCell value="2026-01-15T12:00:00Z" />
          </div>
        </ShowcaseCard>

        <ShowcaseCard title="Switch">
          <Stack gap={3}>
            <FieldLabel className="flex-row items-center gap-2">
              <Switch
                checked={autopay}
                onCheckedChange={(next) => setAutopay(next)}
              />
              Autopay {autopay ? 'on' : 'off'}
            </FieldLabel>
            <FieldLabel className="flex-row items-center gap-2">
              <Switch defaultChecked={false} disabled />
              Disabled
            </FieldLabel>
          </Stack>
        </ShowcaseCard>

        <ShowcaseCard title="ToggleGroup (segmented)">
          <ToggleGroup
            variant="outline"
            spacing={0}
            value={[scope]}
            onValueChange={(next) => {
              const picked = next.at(-1);
              if (picked) setScope(String(picked));
            }}
            aria-label="Scope filter"
          >
            {['all', 'system', 'provider', 'client'].map((value) => (
              <ToggleGroupItem key={value} value={value} className="capitalize">
                {value}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </ShowcaseCard>

        <ShowcaseCard title="Tabs (underline)">
          <Tabs defaultValue="detail" className="gap-4">
            <TabsList aria-label="Record tabs" variant="line">
              <TabsTrigger value="detail">Detail</TabsTrigger>
              <TabsTrigger value="activity">Activity</TabsTrigger>
              <TabsTrigger value="settings">Settings</TabsTrigger>
            </TabsList>
            <TabsContent value="detail">
              <p className="text-sm text-muted-foreground">
                Primary record fields and identifiers.
              </p>
            </TabsContent>
            <TabsContent value="activity">
              <p className="text-sm text-muted-foreground">
                Recent events and audit history.
              </p>
            </TabsContent>
            <TabsContent value="settings">
              <p className="text-sm text-muted-foreground">
                Per-record configuration and preferences.
              </p>
            </TabsContent>
          </Tabs>
        </ShowcaseCard>

        <ShowcaseCard title="FilterBar">
          <FilterBar
            search={search}
            onSearchChange={setSearch}
            searchLabel="Search"
            searchPlaceholder="Search…"
            onClear={
              search || status !== 'all'
                ? () => {
                    setSearch('');
                    setStatus('all');
                  }
                : undefined
            }
          >
            <FilterField label="Status">
              <Select
                value={status}
                onValueChange={(v) => setStatus(v ?? 'all')}
              >
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </FilterField>
          </FilterBar>
        </ShowcaseCard>

        <ShowcaseCard title="Toasts">
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => notify.success('Saved successfully.')}
            >
              Success
            </Button>
            <Button
              variant="outline"
              onClick={() => notify.error('Something went wrong.')}
            >
              Error
            </Button>
            <Button
              variant="outline"
              onClick={() =>
                notify.info('Heads up.', { description: 'With a description.' })
              }
            >
              Info
            </Button>
            <Button
              variant="outline"
              onClick={() => notify.warning('Careful now.')}
            >
              Warning
            </Button>
          </div>
        </ShowcaseCard>

        <ShowcaseCard title="Stepper">
          <Stack gap={4}>
            <Stepper
              steps={[
                { id: 'upload', label: 'Upload' },
                { id: 'map', label: 'Map fields' },
                { id: 'review', label: 'Review' },
                { id: 'done', label: 'Done' },
              ]}
              current={step}
            />
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setStep((s) => Math.max(0, s - 1))}
              >
                Back
              </Button>
              <Button
                size="sm"
                onClick={() => setStep((s) => Math.min(3, s + 1))}
              >
                Next
              </Button>
            </div>
          </Stack>
        </ShowcaseCard>

        <ShowcaseCard title="Dropzone">
          <Stack gap={2}>
            <Dropzone
              multiple
              onFilesSelected={(fs) => {
                setFiles(fs.map((f) => f.name));
                notify.info(`Selected ${fs.length} file(s).`);
              }}
              hint="Any file, up to 10MB"
            />
            {files.length ? (
              <p className="text-xs text-muted-foreground">
                Selected: {files.join(', ')}
              </p>
            ) : null}
          </Stack>
        </ShowcaseCard>

        <ShowcaseCard title="ConfirmDialog">
          <Button variant="destructive" onClick={() => setConfirmOpen(true)}>
            <Trash2 className="size-4" />
            Delete something
          </Button>
          <ConfirmDialog
            open={confirmOpen}
            onOpenChange={setConfirmOpen}
            title="Delete this item?"
            description="This action cannot be undone."
            confirmLabel="Delete"
            destructive
            onConfirm={() => {
              notify.success('Deleted.');
            }}
          />
        </ShowcaseCard>

        <ShowcaseCard title="Timeline">
          <Timeline
            items={[
              {
                id: '1',
                title: 'Invoice created',
                at: '2026-01-15T09:00:00Z',
                detail: 'INV-1001 for Acme Health',
              },
              {
                id: '2',
                title: 'Payment received',
                at: '2026-01-18T14:30:00Z',
              },
              {
                id: '3',
                title: 'Invoice paid',
                at: '2026-01-18T14:31:00Z',
              },
            ]}
          />
        </ShowcaseCard>

        <ShowcaseCard title="EmptyState">
          <EmptyState
            title="Nothing here yet"
            description="Create your first record to get started."
            action={
              <Button size="sm">
                <Plus className="size-4" />
                New record
              </Button>
            }
          />
        </ShowcaseCard>

        <ShowcaseCard title="Skeletons (loading)">
          <Stack gap={2}>
            <Skeleton className="h-5 w-full" />
            <Skeleton className="h-5 w-4/5" />
            <Skeleton className="h-5 w-2/3" />
          </Stack>
        </ShowcaseCard>
      </Grid>

      <Section aria-label="Forms">
        <SectionHeader
          title="Forms"
          description="Every form control in one place, grouped with the FieldSet / FieldGroup / FieldLegend primitives — the vocabulary a real form is built from."
        />
        <Grid cols={2} gap={6}>
          <ShowcaseCard title="Text fields">
            <FieldSet>
              <FieldLegend variant="label">Contact</FieldLegend>
              <FieldGroup>
                <Field>
                  <FieldLabel htmlFor="ks-input">Text input</FieldLabel>
                  <Input id="ks-input" placeholder="Type here…" />
                </Field>
                <Field>
                  <FieldLabel htmlFor="ks-textarea">Notes</FieldLabel>
                  <Textarea
                    id="ks-textarea"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Multi-line free text…"
                  />
                  <FieldDescription>
                    Grows with its content; resizable vertically.
                  </FieldDescription>
                </Field>
                <Field>
                  <FieldLabel htmlFor="ks-textarea-disabled">
                    Notes (disabled)
                  </FieldLabel>
                  <Textarea
                    id="ks-textarea-disabled"
                    disabled
                    defaultValue="Read-only content."
                  />
                </Field>
              </FieldGroup>
            </FieldSet>
          </ShowcaseCard>

          <ShowcaseCard title="Validation states">
            <FieldSet>
              <FieldLegend variant="label">Field states</FieldLegend>
              <FieldGroup>
                <Field>
                  <FieldLabel htmlFor="ks-field-email">Email</FieldLabel>
                  <Input
                    id="ks-field-email"
                    type="email"
                    placeholder="name@example.com"
                  />
                  <FieldDescription>
                    We only use this for billing notifications.
                  </FieldDescription>
                </Field>
                <Field data-invalid>
                  <FieldLabel htmlFor="ks-field-tin">Tax ID</FieldLabel>
                  <Input
                    id="ks-field-tin"
                    aria-invalid
                    defaultValue="12-34"
                    placeholder="12-3456789"
                  />
                  <FieldError
                    errors={[{ message: 'Enter a 9-digit tax ID.' }]}
                  />
                </Field>
                <Field data-invalid>
                  <FieldLabel htmlFor="ks-textarea-invalid">Reason</FieldLabel>
                  <Textarea
                    id="ks-textarea-invalid"
                    aria-invalid
                    defaultValue="too short"
                  />
                  <FieldError
                    errors={[{ message: 'Give at least one full sentence.' }]}
                  />
                </Field>
              </FieldGroup>
            </FieldSet>
          </ShowcaseCard>

          <ShowcaseCard title="Selection">
            <FieldSet>
              <FieldLegend variant="label">Choose one</FieldLegend>
              <FieldGroup>
                <Field>
                  <FieldLabel>Select</FieldLabel>
                  <Select defaultValue="a">
                    <SelectTrigger className="w-48">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="a">Option A</SelectItem>
                      <SelectItem value="b">Option B</SelectItem>
                      <SelectItem value="c">Option C</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field>
                  <FieldLabel>Combobox (searchable single-select)</FieldLabel>
                  <Combobox
                    items={FRUITS}
                    value={fruit}
                    onValueChange={setFruit}
                  >
                    <ComboboxInput
                      placeholder="Search fruit…"
                      aria-label="Fruit"
                    />
                    <ComboboxContent>
                      {(f: string) => (
                        <ComboboxItem key={f} value={f}>
                          {f}
                        </ComboboxItem>
                      )}
                    </ComboboxContent>
                  </Combobox>
                  <FieldDescription>
                    Selected: <code>{fruit ?? '(none)'}</code>
                  </FieldDescription>
                </Field>
                <Field>
                  <FieldLabel>Combobox (multi-select with chips)</FieldLabel>
                  <MultiCombobox
                    items={FRUITS}
                    value={fruits}
                    onValueChange={setFruits}
                  >
                    <ComboboxChips>
                      <ComboboxValue>
                        {(selected: string[]) => (
                          <>
                            {selected.map((f) => (
                              <ComboboxChip
                                key={f}
                                aria-label={f}
                                removeLabel={`Remove ${f}`}
                              >
                                {f}
                              </ComboboxChip>
                            ))}
                            <ComboboxChipsInput
                              placeholder={
                                selected.length ? '' : 'Search fruit…'
                              }
                              aria-label="Fruits"
                            />
                          </>
                        )}
                      </ComboboxValue>
                    </ComboboxChips>
                    <ComboboxContent>
                      {(f: string) => (
                        <ComboboxItem key={f} value={f}>
                          {f}
                        </ComboboxItem>
                      )}
                    </ComboboxContent>
                  </MultiCombobox>
                  <FieldDescription>
                    Selected:{' '}
                    <code>{fruits.length ? fruits.join(', ') : '(none)'}</code>
                  </FieldDescription>
                </Field>
              </FieldGroup>
            </FieldSet>
          </ShowcaseCard>

          <ShowcaseCard title="Toggles & radios">
            <FieldSet>
              <FieldLegend variant="label">Options</FieldLegend>
              <FieldGroup>
                <FieldSet>
                  <FieldLegend variant="label">Checkbox</FieldLegend>
                  <FieldGroup data-slot="checkbox-group">
                    <Field orientation="horizontal">
                      <Checkbox id="cb-unchecked" defaultChecked={false} />
                      <FieldLabel htmlFor="cb-unchecked">Unchecked</FieldLabel>
                    </Field>
                    <Field orientation="horizontal">
                      <Checkbox id="cb-checked" defaultChecked />
                      <FieldLabel htmlFor="cb-checked">Checked</FieldLabel>
                    </Field>
                    <Field orientation="horizontal">
                      <Checkbox id="cb-indeterminate" indeterminate />
                      <FieldLabel htmlFor="cb-indeterminate">
                        Indeterminate
                      </FieldLabel>
                    </Field>
                    <Field orientation="horizontal">
                      <Checkbox id="cb-disabled" defaultChecked disabled />
                      <FieldLabel htmlFor="cb-disabled">
                        Disabled (checked)
                      </FieldLabel>
                    </Field>
                  </FieldGroup>
                </FieldSet>
                <Field>
                  <FieldLabel>Radio group</FieldLabel>
                  <RadioGroup
                    value={plan}
                    onValueChange={(v) => setPlan(String(v))}
                    aria-label="Billing cadence"
                  >
                    {[
                      { value: 'monthly', label: 'Monthly' },
                      { value: 'quarterly', label: 'Quarterly' },
                      { value: 'annual', label: 'Annual' },
                    ].map((opt) => (
                      <FieldLabel
                        key={opt.value}
                        className="flex-row items-center gap-2"
                      >
                        <RadioGroupItem value={opt.value} />
                        {opt.label}
                      </FieldLabel>
                    ))}
                  </RadioGroup>
                </Field>
              </FieldGroup>
            </FieldSet>
          </ShowcaseCard>

          <ShowcaseCard title="Card-radio (generic) — plan">
            <CardRadioGroup
              aria-label="Plan"
              value={cardPlan}
              onValueChange={setCardPlan}
              options={[
                {
                  value: 'starter',
                  title: 'Starter',
                  description: 'For small teams getting set up.',
                },
                {
                  value: 'growth',
                  title: 'Growth',
                  description: 'Scales with your book of business.',
                },
                {
                  value: 'enterprise',
                  title: 'Enterprise',
                  description: 'Dedicated support and custom limits.',
                },
              ]}
            />
          </ShowcaseCard>

          <ShowcaseCard title="DatePicker (ISO in/out, UTC display)">
            <FieldSet>
              <FieldLegend variant="label">Dates</FieldLegend>
              <FieldGroup>
                <Field>
                  <FieldLabel htmlFor="ks-date">Effective date</FieldLabel>
                  <DatePicker
                    id="ks-date"
                    value={date}
                    onValueChange={setDate}
                  />
                  <FieldDescription>
                    Value (ISO): <code>{date}</code>
                  </FieldDescription>
                </Field>
              </FieldGroup>
            </FieldSet>
          </ShowcaseCard>
        </Grid>
      </Section>

      <Section>
        <SectionHeader
          title="Density"
          description="Compact (default), comfortable, and loose row scale."
        />
        <Grid cols={3} gap={6}>
          <div data-density="compact">
            <DensityRows label="compact" />
          </div>
          <div data-density="comfortable">
            <DensityRows label="comfortable" />
          </div>
          <div data-density="loose">
            <DensityRows label="loose" />
          </div>
        </Grid>
      </Section>

      <Section aria-label="Entity-slice components">
        <SectionHeader
          title="Entity-slice components"
          description="Composed building blocks the five entity detail slices share, rendered with mock data (no network)."
        />
        <Grid cols={2} gap={6}>
          <ShowcaseCard title="DownloadCell (3 states)">
            <div className="grid grid-cols-[auto_1fr] items-center gap-x-6 gap-y-3 text-sm">
              {DOWNLOAD_STATES.map(({ state, note }) => (
                <div key={state} className="contents">
                  <DownloadCell
                    state={state}
                    {...(state === 'completed'
                      ? {
                          onDownload: () => {
                            notify.success('Resolving signed URL…');
                          },
                        }
                      : {})}
                    {...(state === 'failed'
                      ? { errorMessage: 'Report generation failed.' }
                      : {})}
                  />
                  <span className="text-muted-foreground">
                    {state} — {note}
                  </span>
                </div>
              ))}
            </div>
          </ShowcaseCard>

          <ShowcaseCard title="NestedDetailTabs">
            <NestedDetailTabs
              label="Member detail"
              tabs={[
                {
                  id: 'detail',
                  label: 'Member Detail',
                  content: (
                    <p className="text-sm text-muted-foreground">
                      Demographics and contact info.
                    </p>
                  ),
                },
                {
                  id: 'enrollment',
                  label: 'Enrollment',
                  content: (
                    <p className="text-sm text-muted-foreground">
                      Plan elections and effective dates.
                    </p>
                  ),
                },
                {
                  id: 'transactions',
                  label: 'Transactions',
                  content: (
                    <p className="text-sm text-muted-foreground">
                      Ledger of premium transactions.
                    </p>
                  ),
                },
              ]}
            />
          </ShowcaseCard>
        </Grid>

        <ShowcaseCard title="RailSubNav">
          <p className="mb-3 text-sm text-muted-foreground">
            The entity detail sub-nav renders in the shell side-nav rail, nested
            under the selected entity. Shown here on a chrome panel; the active
            item carries <code>aria-current</code> and each link is the full
            deep-link path.
          </p>
          <div className="max-w-xs rounded-md bg-chrome p-3">
            <RailSubNav
              sections={DEMO_SECTIONS}
              activeSectionId="bank-accounts"
              basePath="/providers/demo-1"
              entityName="Acme Health Provider"
              backHref="/providers"
              backLabel="All Providers"
              navLabel="Provider sections"
            />
          </div>
        </ShowcaseCard>

        <ShowcaseCard title="BankAccountsSection (account numbers masked)">
          <BankAccountsSection
            rows={BANK_ACCOUNTS}
            canManage
            onCreate={() => {
              notify.info('Open create-account dialog (feature-owned).');
            }}
            onEdit={(row) => {
              notify.info(`Edit ${row.bankName} (feature-owned).`);
            }}
          />
        </ShowcaseCard>

        <ShowcaseCard title="ImportReviewTable (ok / warning / error rows)">
          <ImportReviewTable
            rows={IMPORT_ROWS}
            onFilesSelected={(fs) => {
              notify.info(`Selected ${fs.length} file(s) (demo — no upload).`);
            }}
            accept=".csv,.txt"
            dropzoneHint="CSV or TXT, up to 10MB"
          />
        </ShowcaseCard>
      </Section>

      <Section aria-label="Composed pickers & cards">
        <SectionHeader
          title="Composed pickers & cards"
          description="Address fields, KPI cards, and the billing disbursement-mode picker composed over the generic card-radio."
        />
        <Grid cols={2} gap={6}>
          <ShowcaseCard title="AddressFields (domain-free composite)">
            <AddressFields value={address} onChange={setAddress} />
          </ShowcaseCard>
        </Grid>

        <ShowcaseCard title="KPICard (screen-3 — icon + label + value, muted “—” empty)">
          <Grid cols={3} gap={6}>
            <KPICard
              label="Outstanding"
              value="$128,400"
              icon={DollarSign}
              hint="+4.2% vs last month"
            />
            <KPICard label="Open invoices" value="37" icon={FileText} />
            <KPICard label="Avg. days to pay" value="" icon={Clock} />
          </Grid>
        </ShowcaseCard>

        <ShowcaseCard title="DisbursementModePicker (billing feature over card-radio)">
          <Stack gap={2}>
            <DisbursementModePicker value={mode} onValueChange={setMode} />
            <p className="text-xs text-muted-foreground">
              Selected mode: <code>{mode}</code>
            </p>
          </Stack>
        </ShowcaseCard>
      </Section>

      <Section aria-label="Command & Slider">
        <SectionHeader
          title="Command & Slider"
          description="The two generic primitives added to round out the roster: a filterable command list and a range slider."
        />
        <Grid cols={2} gap={6}>
          <ShowcaseCard title="Command (⌘K dialog palette)">
            <Button variant="outline" onClick={() => setCommandOpen(true)}>
              Open command palette
              <CommandShortcut>⌘K</CommandShortcut>
            </Button>
            <p className="mt-2 text-xs text-muted-foreground">
              Last run: <code>{lastCommand ?? '(none)'}</code>
            </p>
            <CommandDialog open={commandOpen} onOpenChange={setCommandOpen}>
              <Command>
                <CommandInput placeholder="Type a command or search…" />
                <CommandList>
                  <CommandEmpty>No results found.</CommandEmpty>
                  <CommandGroup heading="Actions">
                    <CommandItem
                      value="Open invoice"
                      onSelect={(v) => {
                        setLastCommand(v);
                        setCommandOpen(false);
                      }}
                    >
                      Open invoice
                      <CommandShortcut>⌘O</CommandShortcut>
                    </CommandItem>
                    <CommandItem
                      value="New client"
                      onSelect={(v) => {
                        setLastCommand(v);
                        setCommandOpen(false);
                      }}
                    >
                      New client
                      <CommandShortcut>⌘N</CommandShortcut>
                    </CommandItem>
                  </CommandGroup>
                  <CommandSeparator />
                  <CommandGroup heading="Records">
                    <CommandItem
                      value="Export CSV"
                      onSelect={(v) => {
                        setLastCommand(v);
                        setCommandOpen(false);
                      }}
                    >
                      Export CSV
                    </CommandItem>
                    <CommandItem
                      value="Archive record"
                      onSelect={(v) => {
                        setLastCommand(v);
                        setCommandOpen(false);
                      }}
                    >
                      Archive record
                    </CommandItem>
                  </CommandGroup>
                </CommandList>
              </Command>
            </CommandDialog>
          </ShowcaseCard>

          <ShowcaseCard title="Slider (single / range / disabled)">
            <Stack gap={6}>
              <Field>
                <FieldLabel>Volume</FieldLabel>
                <Slider
                  aria-label="Volume"
                  value={volume}
                  min={0}
                  max={100}
                  onValueChange={(next) =>
                    setVolume(Array.isArray(next) ? [...next] : [next])
                  }
                />
                <FieldDescription>
                  Value: <code>{volume[0]}</code>
                </FieldDescription>
              </Field>
              <Field>
                <FieldLabel>Price range</FieldLabel>
                <Slider
                  aria-label="Price range"
                  value={priceRange}
                  min={0}
                  max={100}
                  onValueChange={(next) =>
                    setPriceRange(Array.isArray(next) ? [...next] : [next])
                  }
                />
                <FieldDescription>
                  Selected: <code>{priceRange.join(' – ')}</code>
                </FieldDescription>
              </Field>
              <Field>
                <FieldLabel>Disabled</FieldLabel>
                <Slider
                  aria-label="Disabled slider"
                  defaultValue={[30]}
                  disabled
                />
              </Field>
            </Stack>
          </ShowcaseCard>
        </Grid>
      </Section>
    </Page>
  );
}

const FRUITS = ['Apple', 'Apricot', 'Banana', 'Blueberry', 'Cherry', 'Mango'];

function DensityRows({ label }: { label: string }) {
  return (
    <div className="rounded-lg border">
      <div
        className="border-b py-2 text-xs font-medium tracking-wide text-muted-foreground uppercase"
        style={{ paddingInline: 'var(--cell-px)' }}
      >
        {label}
      </div>
      {['Acme Health 1', 'Acme Health 2', 'Acme Health 3'].map((name) => (
        <div
          key={name}
          className="flex items-center border-b text-sm last:border-0"
          style={{
            height: 'var(--row-height)',
            paddingInline: 'var(--cell-px)',
          }}
        >
          {name}
        </div>
      ))}
    </div>
  );
}
